import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DropdownDTO } from '../../../types/common/DropdownDTO';
import {
    BranchDetailDTO,
    BranchDTO,
    BranchRegistrationDTO,
    BranchUpdateDTO,
} from '../../../types/branches/configuration/BranchTypes';

/**
 * Estado para configuración de sucursales
 */
interface BranchConfigurationState {
    // Formulario para crear/editar
    form: BranchRegistrationDTO | BranchUpdateDTO;
    // Detalle de una sucursal seleccionada
    detail: BranchDetailDTO | null;
    // Listado paginado
    list: BranchDTO[];
    paginator: any; // Puedes tiparlo según tu backend Page<T>
    // Estados generales
    isLoading: boolean;
    isSubmitting: boolean;
    error: string | null;
    // Dropdowns
    establishmentTypes: DropdownDTO[];
    // Modales
    showRegistrationModal: boolean;
    showViewModal: boolean;
    showEditModal: boolean;
}

const initialState: BranchConfigurationState = {
    form: {
        id: null,
        name: null,
        establishmentTypeId: null,
        establishmentCode: null,
        pointOfSaleCode: null,
        mhEstablishmentCode: null,
        mhPointOfSaleCode: null,
        departmentId: null,
        departmentCode: null,
        municipalityId: null,
        complement: null,
        active: true,
    },
    detail: null,
    list: [],
    paginator: null,
    isLoading: false,
    isSubmitting: false,
    error: null,
    establishmentTypes: [],
    showRegistrationModal: false,
    showViewModal: false,
    showEditModal: false,
};

const branchConfigurationSlice = createSlice({
    name: 'branchConfiguration',
    initialState,
    reducers: {
        // Estado de carga
        setLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
        // Estado de envío (guardar/actualizar)
        setSubmitting(state, action: PayloadAction<boolean>) {
            state.isSubmitting = action.payload;
        },
        // Manejo de errores
        setError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
        },
        // Guardar la lista de sucursales y su paginador
        setBranchList(state, action: PayloadAction<BranchDTO[]>) {
            state.list = action.payload;
        },
        setPaginator(state, action: PayloadAction<any>) {
            state.paginator = action.payload;
        },
        // Cargar detalle de sucursal (para edición o vista)
        setBranchDetail(state, action: PayloadAction<BranchDetailDTO>) {
            state.detail = action.payload;
            state.form = {
                id: action.payload.id,
                name: action.payload.name,
                establishmentTypeId: action.payload.establishmentTypeId,
                establishmentCode: action.payload.establishmentCode,
                pointOfSaleCode: action.payload.pointOfSaleCode,
                mhEstablishmentCode: action.payload.mhEstablishmentCode ?? '',
                mhPointOfSaleCode: action.payload.mhPointOfSaleCode ?? '',
                departmentId: action.payload.departmentId,
                departmentCode: action.payload.departmentCode ?? '',
                municipalityId: action.payload.municipalityId ?? null,
                complement: action.payload.complement,
                active: action.payload.active,
            };
            state.error = null;
        },

        // Actualizar un campo específico del formulario
        updateBranchField(
            state,
            action: PayloadAction<{
                field: keyof BranchRegistrationDTO | keyof BranchUpdateDTO;
                value: any;
            }>
        ) {
            const { field, value } = action.payload;
            state.form = {
                ...state.form,
                [field]: value,
            };
        },
        // Resetear el formulario a su estado inicial
        resetBranchForm(state) {
            state.form = initialState.form;
            state.detail = null;
            state.isSubmitting = false;
            state.error = null;
        },
        // Guardar la lista de tipos de establecimiento
        setEstablishmentTypes(state, action: PayloadAction<DropdownDTO[]>) {
            state.establishmentTypes = action.payload;
        },
        // Control de modales
        setShowRegistrationModal(state, action: PayloadAction<boolean>) {
            state.showRegistrationModal = action.payload;
        },
        setShowViewModal(state, action: PayloadAction<boolean>) {
            state.showViewModal = action.payload;
        },
        setShowEditModal(state, action: PayloadAction<boolean>) {
            state.showEditModal = action.payload;
        },
        updateBranchStatusInList(
            state,
            action: PayloadAction<{ id: number; active: boolean }>
        ) {
            const { id, active } = action.payload;
            state.list = state.list.map((branch) =>
                branch.id === id ? { ...branch, active } : branch
            );
        }
    },
});

export const {
    setLoading,
    setSubmitting,
    setError,
    setBranchList,
    setPaginator,
    setBranchDetail,
    updateBranchField,
    resetBranchForm,
    setEstablishmentTypes,
    setShowRegistrationModal,
    setShowViewModal,
    setShowEditModal,
    updateBranchStatusInList,
} = branchConfigurationSlice.actions;

export default branchConfigurationSlice.reducer;
